import React, { useEffect, useRef, useState } from 'react';

import {
  Bar,
  CartesianGrid,
  Cell,
  ComposedChart,
  LabelList,
  ReferenceLine,
  ResponsiveContainer,
  Scatter,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import TableChartOutlinedIcon from '@mui/icons-material/TableChartOutlined';
import {
  Autocomplete,
  Box,
  Button,
  Card,
  Tooltip as MuiTooltip,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';

import { SeriesKey } from './BookedPredicted';
import ChartTooltip from './ChartTooltip';
import ChartValueLabel from './ChartValueLabel';
import ClearSelectionPill from './ClearSelectionPill';
import DepartureCharts from './DepartureCharts';
import { departureActionDetail } from './departureActions';
import {
  ChartUnit,
  DeparturePoint,
  PeriodPoint,
  TourRow,
  flagLabels,
  marginDomain,
  marginTicks,
  money,
  moneyShort,
  pct,
} from './logic';
import { actionFlag } from './marginBands';
import { reviewStatusSx, tcc } from './palette';
import { reviewGroupTooltip, reviewStatuses, statusOf } from './reviewLogic';
import { ReviewStatus } from './reviewTypes';
import { CostBasis } from './types';
import useValueLabelLayout from './useValueLabelLayout';

interface ChartPoint extends PeriodPoint {
  lfPct: number | null;
  plfPct: number | null;
  bePct: number | null;
}

const toChart = (points: Array<PeriodPoint>): Array<ChartPoint> =>
  points.map((p) => ({
    ...p,
    lfPct: p.cap ? Math.round((p.lf ?? 0) * 1000) / 10 : null,
    plfPct: p.cap ? Math.round((p.plf ?? 0) * 1000) / 10 : null,
    bePct: p.beLf == null ? null : Math.round(p.beLf * 1000) / 10,
  }));

const rangeText = (p: PeriodPoint) => {
  const f = (d: string) =>
    new Date(`${d}T00:00:00`).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
  return p.from === p.to ? f(p.from) : `${f(p.from)} – ${f(p.to)}`;
};

function PeriodTooltip({
  active,
  payload,
  selectable = true,
}: {
  active?: boolean;
  payload?: Array<{ payload: ChartPoint }>;
  selectable?: boolean;
}) {
  const p = payload?.[0]?.payload;
  if (!active || !p) {
    return null;
  }
  const line = (label: string, booked: React.ReactNode, predicted: React.ReactNode) => (
    <TableRow>
      <TableCell sx={{ color: 'text.secondary', pl: 0 }}>{label}</TableCell>
      <TableCell align="right">{booked}</TableCell>
      <TableCell align="right">{predicted}</TableCell>
    </TableRow>
  );
  const tone = (rate: number | null) => <span>{pct(rate)}</span>;
  return (
    <ChartTooltip maxWidth={340}>
      <Paper variant="outlined" sx={{ px: 1.5, py: 1 }}>
        <Typography variant="subtitle2">{rangeText(p)}</Typography>
        {p.n === 0 ? (
          <Typography variant="caption" color="text.secondary">
            No Departures
          </Typography>
        ) : (
          <>
            <Table
              size="small"
              sx={{
                '& td': { border: 0, py: 0.25, px: 1, fontSize: 12, fontVariantNumeric: 'tabular-nums' },
                '& th': { border: 0, py: 0.25, px: 1, fontSize: 11, bgcolor: 'transparent' },
              }}
            >
              <TableHead>
                <TableRow>
                  <TableCell sx={{ pl: 0 }} />
                  <TableCell align="right" sx={{ color: tcc.bookedText, fontWeight: 700 }}>
                    Booked
                  </TableCell>
                  <TableCell align="right" sx={{ color: tcc.predictedText, fontWeight: 700 }}>
                    Predicted
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {line('Load Factor', pct(p.lf), pct(p.plf))}
                {line(
                  'Travellers',
                  `${Math.round(p.pax).toLocaleString('en-AU')} / ${p.cap.toLocaleString('en-AU')}`,
                  Math.round(p.ppax).toLocaleString('en-AU'),
                )}
                {line('TTV', money(p.ttv), money(p.pttv))}
                {line('Cost', money(p.cost), money(p.pcost))}
                {line('Margin', money(p.m), money(p.pmv))}
                {line('Margin Rate', tone(p.mp), tone(p.pm))}
              </TableBody>
            </Table>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
              Departures: {p.n.toLocaleString('en-AU')}
              {p.beLf != null ? ` · break-even load factor ${pct(p.beLf)}` : ''}
              {p.final ? ` · ${p.final} past handback (final)` : ''}
            </Typography>
            {(p.red > 0 || p.amber > 0 || p.empty > 0) && (
              <Typography variant="caption" display="block">
                {p.red > 0 && <span style={{ color: tcc.redText }}>● {p.red} need action </span>}
                {p.amber > 0 && <span style={{ color: tcc.amberText }}>● {p.amber} watch </span>}
                {p.empty > 0 && <span style={{ color: tcc.empty }}>○ {p.empty} empty near handback</span>}
              </Typography>
            )}
            {selectable && (
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                Click to show only these departures
              </Typography>
            )}
          </>
        )}
        <Typography variant="caption" display="block">
          Margin = TTV minus supplier cost, minus ICS at cost-plus where the tour has one
        </Typography>
      </Paper>
    </ChartTooltip>
  );
}

const statusInitials: Record<ReviewStatus, string> = {
  Urgent: 'U',
  'Needs Action': 'A',
  'Assessment Incomplete': 'I',
  Watch: 'W',
  Monitor: 'M',
  'On Track': 'O',
  Final: 'F',
  'Not in review': 'N',
};

/** Keep sizing and rendering on the same status selection, including older snapshots. */
export function periodPillStatuses(p: PeriodPoint, basis: CostBasis) {
  const all = reviewStatuses.filter((status) => p.departures?.some((d) => statusOf(d, basis) === status));
  const attention = all.filter((status) =>
    ['Urgent', 'Needs Action', 'Assessment Incomplete', 'Watch'].includes(status),
  );
  return attention.length ? attention : all;
}
export function periodPillLayout(points: Array<PeriodPoint>, basis: CostBasis) {
  const count = Math.max(
    0,
    ...points.map((p) => {
      if (!p.n) return 0;
      const statuses = periodPillStatuses(p, basis);
      return statuses.length || [p.red, p.amber, p.empty].filter((n) => n > 0).length || 1;
    }),
  );
  // Two 13px lines plus 4px padding per pill, with a 2px gap between pills.
  const pillHeight = count * 30 + Math.max(0, count - 1) * 2;
  const axisHeight = 24 + pillHeight;
  return { pillHeight, axisHeight, chartHeight: 128 + axisHeight };
}

/** X-axis tick with a red dot under periods that hold a departure needing action (same cue as the heatmap). */
function PeriodTick({
  x,
  y,
  payload,
  points,
  onSelect,
  slotWidth,
  chartWidth,
  basis,
  tickStep,
  pillHeight,
}: {
  pillHeight: number;
  tickStep: number;
  basis: CostBasis;
  slotWidth: number;
  chartWidth: number;
  onSelect?: (point: PeriodPoint) => void;
  x?: number | string;
  y?: number | string;
  payload?: { value?: unknown; index: number };
  points: Array<ChartPoint>;
}) {
  const p = points[payload?.index ?? -1];
  return (
    <g
      transform={`translate(${x},${y})`}
      role="button"
      tabIndex={0}
      aria-label={`Filter Departures to ${p?.label || ''}`}
      onClick={(event) => {
        event.stopPropagation();
        if (p?.n) onSelect?.(p);
      }}
      onKeyDown={(event) => {
        if (event.key === 'Enter' && p?.n) onSelect?.(p);
      }}
    >
      <text dy={12} textAnchor="middle" fill={tcc.axis} fontSize={11}>
        {(payload?.index ?? 0) % tickStep === 0 ? String(payload?.value ?? '') : ''}
      </text>
      {p?.n > 0 && (
        <foreignObject
          x={-Math.max(0, Math.min(slotWidth, 2 * (Number(x) - 4), 2 * (chartWidth - Number(x) - 4))) / 2}
          y={20}
          width={Math.max(0, Math.min(slotWidth, 2 * (Number(x) - 4), 2 * (chartWidth - Number(x) - 4)))}
          height={pillHeight}
        >
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'minmax(0, 1fr)',
              gap: 0.25,
              fontSize: 11,
              lineHeight: '13px',
              textAlign: 'center',
            }}
          >
            {p.departures?.some((d) => statusOf(d, basis)) ? (
              (() => {
                return periodPillStatuses(p, basis).map((status) => (
                  <MuiTooltip
                    key={status}
                    title={
                      <Box sx={{ whiteSpace: 'pre-line' }}>
                        {reviewGroupTooltip(
                          p.departures.filter((d) => statusOf(d, basis) === status),
                          basis,
                        )}
                      </Box>
                    }
                  >
                    <Box
                      data-chart-action-pill
                      aria-label={`${status} ${p.departures.filter((d) => statusOf(d, basis) === status).length}`}
                      sx={{
                        borderRadius: 2,
                        py: 0.25,
                        ...reviewStatusSx(status),
                      }}
                    >
                      <Box component="span" sx={{ display: 'block', whiteSpace: 'normal' }}>
                        {slotWidth < 64 ? statusInitials[status] : status.split(' ')[0]}
                      </Box>
                      <Box component="span" sx={{ display: 'block', whiteSpace: 'normal' }}>
                        {[
                          ...(slotWidth < 64 ? [] : status.split(' ').slice(1)),
                          String(p.departures.filter((d) => statusOf(d, basis) === status).length),
                        ].join(' ')}
                      </Box>
                    </Box>
                  </MuiTooltip>
                ));
              })()
            ) : (
              <>
                {[
                  [p.red, 'Needs Action', tcc.redText, 'rgba(208,59,59,.14)'],
                  [p.amber, 'Watch', tcc.amberText, 'rgba(250,178,25,.18)'],
                  [p.empty, 'No Bookings', 'text.secondary', 'rgba(99,87,121,.14)'],
                ]
                  .filter(([count]) => Number(count) > 0)
                  .map(([count, label, color, bgcolor]) => (
                    <Box
                      key={String(label)}
                      data-chart-action-pill
                      title={(p.departures || [])
                        .filter((d) => flagLabels[actionFlag(d, basis)] === label)
                        .map((d) => departureActionDetail(d, basis, true))
                        .join('\n')}
                      sx={{ borderRadius: 2, color: String(color), bgcolor: String(bgcolor), py: 0.25 }}
                    >
                      <Box component="span" sx={{ display: 'block', whiteSpace: 'normal' }}>
                        {String(label).split(' ')[0]}
                      </Box>
                      <Box component="span" sx={{ display: 'block', whiteSpace: 'normal' }}>
                        {[...String(label).split(' ').slice(1), String(count)].join(' ')}
                      </Box>
                    </Box>
                  ))}
                {!p.red && !p.amber && !p.empty && (
                  <Box
                    data-chart-action-pill
                    title={(p.departures || [])
                      .filter(
                        (d) =>
                          actionFlag(d, basis) ===
                          (p.onTrack ? 'green' : p.early ? 'early' : p.final ? 'handed back' : 'unavailable'),
                      )
                      .map((d) => departureActionDetail(d, basis, true))
                      .join('\n')}
                    sx={{
                      borderRadius: 2,
                      p: 0.25,
                      color: p.onTrack ? tcc.greenText : 'text.secondary',
                      bgcolor: p.onTrack ? 'rgba(99,127,121,.14)' : 'rgba(123,132,144,.14)',
                    }}
                  >
                    <Box component="span" sx={{ display: 'block', whiteSpace: 'normal' }}>
                      {p.onTrack ? 'On' : p.early ? 'No Bookings' : p.final ? 'Past' : 'No'}
                    </Box>
                    <Box component="span" sx={{ display: 'block', whiteSpace: 'normal' }}>
                      {p.onTrack
                        ? `Track ${p.onTrack}`
                        : p.early
                          ? `Yet ${p.early}`
                          : p.final
                            ? `Handback ${p.final}`
                            : 'Action'}
                    </Box>
                  </Box>
                )}{' '}
              </>
            )}
          </Box>
        </foreignObject>
      )}
    </g>
  );
}

export function BreakevenMark({ cx, cy, width }: { cx?: number; cy?: number; width: number }) {
  const theme = useTheme();
  if (cx == null || cy == null || !Number.isFinite(cy)) {
    return null;
  }
  const coordinates = { x1: cx - width / 2, x2: cx + width / 2, y1: cy, y2: cy };
  return (
    <g>
      <line {...coordinates} stroke={theme.palette.background.paper} strokeWidth={4} />
      <line data-chart-reference-mark {...coordinates} stroke={tcc.breakeven} strokeWidth={2} strokeDasharray="4 3" />
    </g>
  );
}

const pickIndex = (state: unknown): number | null => {
  const s = state as { activeIndex?: number | string | null; activeTooltipIndex?: number | string | null } | null;
  const index = Number(s?.activeIndex ?? s?.activeTooltipIndex);
  return Number.isFinite(index) ? index : null;
};

/** Leave 28px for each pill plus a 4px gap, after the axes and padding. */
export function periodChartLayout(availableWidth: number, count: number) {
  const plotWidth = Math.max(availableWidth, count * 32 + 112);
  return { plotWidth, slotWidth: Math.min(100, (plotWidth - 112) / Math.max(1, count) - 4) };
}

/**
 * Bar charts by departure month or week: load factor and margin, Current Booked Numbers next to predicted at handback,
 * with the break-even load factor marked on each period. They follow every filter except a selected period,
 * which they highlight instead.
 */
export default function PeriodCharts({
  points,
  unit,
  onUnitChange,
  selected,
  onSelect,
  departurePoints,
  basis,
  tour,
  tours,
  onTourChange,
  onDepartureSelect,
  emptyText = 'No departures match the selected filters.',
  selectionLabel,
  showTourPicker = true,
}: {
  departurePoints: Array<DeparturePoint>;
  basis: CostBasis;
  tour: TourRow | null;
  tours: Array<TourRow>;
  onTourChange: (id: string | null) => void;
  onDepartureSelect: (id: string | null) => void;
  emptyText?: string;
  /** What is selected (tour, plus departure when one is picked), shown in the clear pill. */
  selectionLabel?: string | null;
  showTourPicker?: boolean;
  points: Array<PeriodPoint>;
  unit: ChartUnit;
  onUnitChange: (unit: ChartUnit) => void;
  selected: string | null;
  onSelect: (point: PeriodPoint | null) => void;
}) {
  const chartRoot = useRef<HTMLDivElement>(null);
  useValueLabelLayout(chartRoot);
  const theme = useTheme();
  const grid = theme.palette.mode === 'dark' ? tcc.grid.dark : tcc.grid.light;
  const [asTable, setAsTable] = useState(false);
  const data = toChart(points);
  const [chartWidth, setChartWidth] = useState(560);
  const availableRoot = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = availableRoot.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(([entry]) => setChartWidth(entry.contentRect.width));
    observer.observe(el);
    return () => observer.disconnect();
  }, [asTable, unit, data.length > 0]);
  const { plotWidth, slotWidth } = periodChartLayout(chartWidth, data.length);
  const { pillHeight, axisHeight, chartHeight } = periodPillLayout(points, basis);
  const tickStep = Math.max(1, Math.ceil((data.length * 80) / Math.max(1, plotWidth - 112)));
  // Value labels only where there is room: by month, on a wide enough screen.
  const roomy = useMediaQuery(theme.breakpoints.up('md'));
  const labels = roomy && unit === 'month' && data.length <= 13;
  const markWidth = Math.max(10, Math.min(36, 600 / Math.max(1, data.length) - 10));
  const click = (state: unknown) => {
    const index = pickIndex(state);
    if (tour || index == null || !data[index] || data[index].n === 0) {
      return;
    }
    onSelect(selected === data[index].key ? null : data[index]);
  };
  const barOpacity = (p: ChartPoint) => (selected && p.key !== selected ? 0.35 : 1);
  const shared = {
    data,
    margin: { top: 32, right: 8, bottom: 8, left: 0 },
    onClick: click,
    barGap: 2,
    barCategoryGap: unit === 'month' ? '22%' : '14%',
    style: { cursor: 'pointer' },
  };
  const xAxis = (
    <XAxis
      dataKey="label"
      padding={{ left: 24, right: 24 }}
      interval={0}
      tickLine={false}
      axisLine={{ stroke: '#c3c2b7' }}
      height={axisHeight}
      tick={(props) => (
        <PeriodTick
          {...props}
          tickStep={tickStep}
          pillHeight={pillHeight}
          slotWidth={slotWidth}
          chartWidth={plotWidth}
          points={data}
          basis={basis}
          onSelect={(p) => onSelect(selected === p.key ? null : p)}
        />
      )}
    />
  );
  const tooltip = (
    <Tooltip
      content={<PeriodTooltip selectable={!tour} />}
      offset={0}
      allowEscapeViewBox={{ x: true, y: true }}
      wrapperStyle={{ zIndex: 10, pointerEvents: 'none', transform: 'none', top: 0, left: 0 }}
      cursor={{ fill: 'rgba(28, 92, 171, 0.06)' }}
    />
  );
  const options = [{ id: '', name: 'Choose a Tour' }, ...tours.map((t) => ({ id: t.id, name: t.name }))];
  const empty = unit !== 'departure' && data.every((p) => p.cap === 0 && p.n === 0);

  return (
    <Card ref={chartRoot} variant="outlined" sx={{ p: { xs: 1.5, md: 2 }, mt: 2 }}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        justifyContent="space-between"
        gap={1}
      >
        <Box>
          <Typography
            variant="h6"
            sx={{ bgcolor: 'action.hover', borderLeft: '4px solid', borderColor: 'divider', p: 1 }}
          >
            {unit === 'departure'
              ? 'Load Factor and Margin by Departure Date'
              : `All Tours: Load Factor and Margin by Departure ${unit === 'week' ? 'Week' : 'Month'}`}
          </Typography>
          <Stack direction="row" flexWrap="wrap" useFlexGap gap={1} alignItems="center" sx={{ mt: 1, mb: 1 }}>
            <Autocomplete
              size="small"
              options={options}
              value={options.find((option) => option.id === tour?.id) || options[0]}
              isOptionEqualToValue={(a, b) => a.id === b.id}
              getOptionLabel={(option) => option.name}
              onChange={(_, option) => onTourChange(option?.id || null)}
              disableClearable
              renderInput={(params) => <TextField {...params} label="Open Tour Drill-Down" />}
              sx={{ display: showTourPicker ? 'block' : 'none', width: { xs: 240, sm: 320 }, maxWidth: '100%' }}
            />
            {tour && showTourPicker && (
              <ClearSelectionPill
                detail={selectionLabel ?? tour.name}
                onClick={() => onTourChange(null)}
                sx={{ maxWidth: { xs: '100%', sm: 420 } }}
              />
            )}
          </Stack>
          <Stack direction="row" flexWrap="wrap" gap={2} sx={{ mt: 0.5 }}>
            <SeriesKey kind="booked" label="Current Booked Numbers" />
            <SeriesKey kind="predicted" label="Predicted at Handback" />
            {tour && (
              <Stack direction="row" alignItems="center" spacing={0.5} component="span">
                <Box component="span" sx={{ width: 18, borderTop: `1.5px solid ${tcc.axis}` }} />
                <Typography variant="caption">Max Capacity</Typography>
              </Stack>
            )}
            <SeriesKey kind="breakeven" label={tour ? 'Break-Even' : 'Break-Even Load Factor'} />
          </Stack>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          {selected && !tour && (
            <ClearSelectionPill
              action={`Show All ${unit === 'week' ? 'Weeks' : 'Months'}`}
              onClick={() => onSelect(null)}
            />
          )}
          <Button
            size="small"
            variant={asTable ? 'contained' : 'outlined'}
            startIcon={<TableChartOutlinedIcon fontSize="small" />}
            onClick={() => setAsTable(!asTable)}
            aria-pressed={asTable}
          >
            Table
          </Button>
          {unit !== 'departure' && (
            <ToggleButtonGroup
              size="small"
              exclusive
              value={unit}
              onChange={(_, value: ChartUnit | null) => value && onUnitChange(value)}
              aria-label="Group departures by"
            >
              {tour && <ToggleButton value="departure">Departure</ToggleButton>}
              <ToggleButton value="month">Month</ToggleButton>
              <ToggleButton value="week">Week</ToggleButton>
            </ToggleButtonGroup>
          )}
        </Stack>
      </Stack>
      {unit !== 'departure' && (
        <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 1 }}>
          Number of departures in each {unit === 'week' ? 'week' : 'month'} by action. Hover a pill to see which
          departures.
        </Typography>
      )}
      {unit !== 'departure' && slotWidth < 64 && data.some((p) => p.departures?.some((d) => statusOf(d, basis))) && (
        <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 1 }}>
          Compact pills: U Urgent, A Needs Action, I Assessment Incomplete, W Watch, M Monitor, O On Track, F Final, N
          Not in review.
        </Typography>
      )}
      {unit === 'departure' ? (
        <DepartureCharts
          points={departurePoints}
          basis={basis}
          asTable={asTable}
          emptyText={emptyText}
          selected={selected}
          onSelect={onDepartureSelect}
        />
      ) : empty ? (
        <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
          {emptyText}
        </Typography>
      ) : asTable ? (
        <Box sx={{ overflowX: 'auto', mt: 1.5 }}>
          <Table size="small" aria-label={`Booked and predicted by departure ${unit}`}>
            <TableHead>
              <TableRow>
                {[
                  'Departure ' + (unit === 'week' ? 'Week' : 'Month'),
                  'Departures',
                  'Current LF',
                  'Predicted LF',
                  'Break-Even LF',
                  'Current TTV',
                  'Predicted TTV',
                  'Current Margin',
                  'Predicted Margin',
                  'Needs Action / Watch',
                ].map((label) => (
                  <TableCell key={label} sx={{ fontWeight: 600, whiteSpace: 'normal' }}>
                    {label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody sx={{ '& td': { fontVariantNumeric: 'tabular-nums', whiteSpace: 'normal' } }}>
              {data.map((p) => (
                <TableRow
                  key={p.key}
                  hover
                  selected={p.key === selected}
                  onClick={() => !tour && p.n && onSelect(selected === p.key ? null : p)}
                  sx={{ cursor: p.n ? 'pointer' : undefined }}
                >
                  <TableCell>{rangeText(p)}</TableCell>
                  <TableCell>{p.n}</TableCell>
                  <TableCell>{pct(p.lf)}</TableCell>
                  <TableCell>{pct(p.plf)}</TableCell>
                  <TableCell>{pct(p.beLf)}</TableCell>
                  <TableCell>{money(p.ttv)}</TableCell>
                  <TableCell>{money(p.pttv)}</TableCell>
                  <TableCell>
                    {money(p.m)} · {pct(p.mp)}
                  </TableCell>
                  <TableCell>
                    {money(p.pmv)} · {pct(p.pm)}
                  </TableCell>
                  <TableCell>
                    {p.red} / {p.amber}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      ) : (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: 'minmax(0, 1fr)', lg: 'repeat(2, minmax(0, 1fr))' },
            gap: 2,
            mt: 1.5,
          }}
        >
          <Box ref={availableRoot} sx={{ minWidth: 0 }}>
            <Typography variant="subtitle2">Load Factor</Typography>
            <Typography variant="caption" color="text.secondary">
              Share of seats filled, for departures on sale or with bookings
            </Typography>
            <Box
              sx={{ mt: 2, height: 280, overflowX: 'auto', '& *:focus:not(:focus-visible)': { outline: 'none' } }}
              data-testid="command-centre-lf-chart"
            >
              <ResponsiveContainer
                width={plotWidth}
                height={chartHeight}
                initialDimension={{ width: 560, height: chartHeight }}
              >
                <ComposedChart {...shared}>
                  <CartesianGrid vertical={false} stroke={grid} />
                  {xAxis}
                  <YAxis
                    domain={[0, 100]}
                    ticks={[0, 25, 50, 75, 100]}
                    tickFormatter={(v) => `${v}%`}
                    width={40}
                    tickLine={false}
                    axisLine={false}
                    tick={{ fill: tcc.axis, fontSize: 11 }}
                  />
                  {tooltip}
                  <Bar
                    dataKey="lfPct"
                    name="Current Booked Numbers"
                    fill={tcc.booked}
                    stroke={theme.palette.mode === 'dark' ? '#c9d7e5' : undefined}
                    strokeWidth={1.5}
                    radius={[4, 4, 0, 0]}
                    isAnimationActive={false}
                  >
                    {data.map((p) => (
                      <Cell key={p.key} fillOpacity={barOpacity(p)} />
                    ))}
                    {labels && <LabelList dataKey="lfPct" content={<ChartValueLabel />} />}
                  </Bar>
                  <Bar
                    dataKey="plfPct"
                    name="Predicted at Handback"
                    fill={tcc.predicted}
                    stroke={theme.palette.mode === 'dark' ? '#88b8e4' : undefined}
                    radius={[4, 4, 0, 0]}
                    isAnimationActive={false}
                  >
                    {data.map((p) => (
                      <Cell key={p.key} fillOpacity={barOpacity(p)} />
                    ))}
                    {labels && <LabelList dataKey="plfPct" content={<ChartValueLabel kind="predicted" />} />}
                  </Bar>
                  <Scatter
                    dataKey="bePct"
                    name="Break-Even Load Factor"
                    isAnimationActive={false}
                    shape={(props: { cx?: number; cy?: number }) => <BreakevenMark {...props} width={markWidth} />}
                  />
                </ComposedChart>
              </ResponsiveContainer>
            </Box>
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="subtitle2">Margin</Typography>
            <Typography variant="caption" color="text.secondary">
              Margin after supplier cost and ICS at cost-plus (ICS tours only)
            </Typography>
            <Box
              sx={{ mt: 2, height: 280, overflowX: 'auto', '& *:focus:not(:focus-visible)': { outline: 'none' } }}
              data-testid="command-centre-margin-chart"
            >
              <ResponsiveContainer
                width={plotWidth}
                height={chartHeight}
                initialDimension={{ width: 560, height: chartHeight }}
              >
                <ComposedChart {...shared}>
                  <CartesianGrid vertical={false} stroke={grid} />
                  {xAxis}
                  <YAxis
                    domain={marginDomain(points.flatMap((p) => [p.m, p.pmv]))}
                    ticks={marginTicks(points.flatMap((p) => [p.m, p.pmv]))}
                    padding={{ bottom: labels && points.some((p) => p.m < 0 || p.pmv < 0) ? 20 : 0 }}
                    tickFormatter={(v) => moneyShort(v)}
                    width={52}
                    tickLine={false}
                    axisLine={false}
                    tick={{ fill: tcc.axis, fontSize: 11 }}
                  />
                  <ReferenceLine y={0} stroke="#c3c2b7" />
                  {tooltip}
                  <Bar
                    dataKey="m"
                    name="Current Booked Numbers"
                    fill={tcc.booked}
                    stroke={theme.palette.mode === 'dark' ? '#c9d7e5' : undefined}
                    strokeWidth={1.5}
                    radius={[4, 4, 0, 0]}
                    isAnimationActive={false}
                  >
                    {data.map((p) => (
                      <Cell key={p.key} fillOpacity={barOpacity(p)} />
                    ))}
                    {labels && <LabelList dataKey="m" content={<ChartValueLabel format="money" />} />}
                  </Bar>
                  <Bar
                    dataKey="pmv"
                    name="Predicted at Handback"
                    fill={tcc.predicted}
                    stroke={theme.palette.mode === 'dark' ? '#88b8e4' : undefined}
                    radius={[4, 4, 0, 0]}
                    isAnimationActive={false}
                  >
                    {data.map((p) => (
                      <Cell key={p.key} fillOpacity={barOpacity(p)} />
                    ))}
                    {labels && (
                      <LabelList dataKey="pmv" content={<ChartValueLabel kind="predicted" format="money" />} />
                    )}
                  </Bar>
                </ComposedChart>
              </ResponsiveContainer>
            </Box>
          </Box>
        </Box>
      )}
    </Card>
  );
}
